import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, viewChild, ViewChild } from '@angular/core';
import { AddNodeDropdownState, AddNodeDropdownStore, enterTarget, showsCreateRow } from './add-node-dropdown.store';
import { Point } from '../../models/point';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Observable, Subject, switchMap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { BomItem } from '../../models/bom-item.model';

export interface BomItemSelected {
  bomItemId?: number;
  name: string;
}

@Component({
  selector: 'add-node-dropdown',
  templateUrl: './add-node-dropdown.component.html',
  styleUrl: 'add-node-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.left.px]': 'left',
    '[style.top.px]': 'top',
    '(clickOutside)': 'onClickOutside()'
  },
  hostDirectives: [
    {
      directive: ClickOutsideDirective,
      outputs: ['clickOutside']
    }
  ],
  imports: [AsyncPipe, ClickOutsideDirective]
})
export class AddNodeDropdownComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  set openAt(value: Point | undefined) {
    this.store.openAt(value);

    this.left = value?.x ?? 0;
    this.top = value?.y ?? 0;
  }
  get openAt() { return this._openAt; }
  private _openAt: Point | undefined;

  @Output()
  readonly closed = new EventEmitter<void>();

  @Output()
  readonly bomItemSelected = new EventEmitter<BomItemSelected>();

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  left = 0;
  top = 0;

  readonly state$: Observable<AddNodeDropdownState>;

  private readonly searchTerm$ = new Subject<string>();

  constructor(
    private store: AddNodeDropdownStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    this.store.init();

    // Same rule as the list pages: the fetch waits for typing to settle, and switchMap
    // drops a superseded request so an older answer never overwrites a newer one
    this.searchTerm$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => this.store.fetch(term)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.inputElement()!.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  open(x: number, y: number): void {
    this.store.open(x, y);
  }

  onSearchChange(search: string): void {
    this.store.setSearch(search);
    this.searchTerm$.next(search);
  }

  showsCreateRow = showsCreateRow;

  // Enter takes the highlighted row, an exact match or the create row — never a name
  // the list had not answered yet (0110)
  onEnter(): void {
    const target = enterTarget(this.store.state);

    if (target === 'create') {
      this.onCreateClick();
    } else if (target) {
      this.onItemClick(target.item);
    }
  }

  // the visible create row: no bomItemId, the parent creates the item under the typed name (JIT BOM)
  onCreateClick(): void {
    const event: BomItemSelected = {
      bomItemId: undefined,
      name: this.store.state.search!.trim()
    };

    this.bomItemSelected.emit(event);

    this.close();
  }

  // on the input's keydown: the caret stays put, a held key repeats
  onArrowDown(event: Event): void {
    event.preventDefault();
    this.store.highlightNext();
  }

  onArrowUp(event: Event): void {
    event.preventDefault();
    this.store.highlightPrevious();
  }

  onItemClick(item: BomItem): void {
    const event: BomItemSelected = {
      bomItemId: item.id,
      name: item.name
    };

    this.bomItemSelected.emit(event);

    this.close();
  }

  onClickOutside(): void {
    this.close();
  }

  private close(): void {
    this.store.close();
    this.closed.emit();
  }
}
